import React, {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { supabase } from '@/integrations/supabase/client';
import { isWhiteLabelHost } from '@/utils/appUrl';
import {
  loadSupplierCustomization,
  saveSupplierCustomization,
} from '@/utils/supplierCustomizationCache';

/**
 * Identidade white-label resolvida a partir do host (domínio próprio do
 * fornecedor, subdomínio *.ameup.com.br ou host genérico com slug na URL).
 *
 * Motivação: em domínios próprios NENHUM ativo da marca AmeUp pode aparecer —
 * nem no primeiro carregamento, antes do React resolver a loja. Este provider
 * limpa os ícones/manifest estáticos do index.html de forma síncrona e aplica
 * a identidade do fornecedor assim que ela é conhecida.
 */
export interface WhiteLabelBranding {
  /** Host atual não pertence à AmeUp (domínio próprio / white-label). */
  isWhiteLabel: boolean;
  supplierSlug: string | null;
  supplierName: string | null;
  logoUrl: string | null;
  /** Ainda buscando a identidade do fornecedor. */
  isResolving: boolean;
}

const DEFAULT_BRANDING: WhiteLabelBranding = {
  isWhiteLabel: false,
  supplierSlug: null,
  supplierName: null,
  logoUrl: null,
  isResolving: false,
};

const WhiteLabelBrandingContext = createContext<WhiteLabelBranding>(DEFAULT_BRANDING);

export const useWhiteLabelBranding = (): WhiteLabelBranding =>
  useContext(WhiteLabelBrandingContext);

const AMEUP_ICON_SELECTOR =
  'link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"], link[rel="mask-icon"]';

const MARKER = 'data-white-label-branding';

/** Descobre o content-type do favicon a partir da extensão do arquivo. */
export const guessIconMimeType = (url: string): string | undefined => {
  try {
    const path = new URL(url, window.location.href).pathname.toLowerCase();
    if (path.endsWith('.svg')) return 'image/svg+xml';
    if (path.endsWith('.png')) return 'image/png';
    if (path.endsWith('.webp')) return 'image/webp';
    if (path.endsWith('.jpg') || path.endsWith('.jpeg')) return 'image/jpeg';
    if (path.endsWith('.ico')) return 'image/x-icon';
    return undefined;
  } catch {
    return undefined;
  }
};

/** Remove qualquer ícone/manifest da AmeUp herdado do index.html. */
const stripAmeupHeadAssets = () => {
  document.head.querySelectorAll<HTMLLinkElement>(AMEUP_ICON_SELECTOR).forEach((el) => {
    if (el.hasAttribute(MARKER)) return;
    // Ícones injetados por Helmet (páginas da loja) já são do fornecedor.
    if (el.hasAttribute('data-rh')) return;
    el.remove();
  });

  document.head
    .querySelectorAll<HTMLLinkElement>('link[rel="manifest"]')
    .forEach((el) => {
      if (!el.hasAttribute(MARKER)) el.remove();
    });

  if (document.title.includes('AmeUp')) {
    document.title = 'Loja Digital';
  }

  // Dados estruturados da marca AmeUp não podem existir no domínio do fornecedor.
  document.head
    .querySelectorAll<HTMLScriptElement>('script[type="application/ld+json"]')
    .forEach((el) => {
      if (!el.hasAttribute('data-rh') && el.textContent?.includes('ameup.com.br')) {
        el.remove();
      }
    });
};

/** Extrai o slug de rotas públicas do tipo /loja/:slug ou /cardapio/:slug. */
const slugFromPath = (): string | null => {
  const match = window.location.pathname.match(/^\/(?:loja|cardapio)\/([^/]+)/);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
};

const resolveSupplierIdentity = async (
  hostname: string,
): Promise<{ slug: string | null; name: string | null; logoUrl: string | null } | null> => {
  let slug: string | null = null;
  let name: string | null = null;

  // 1) Domínio próprio → RPC pública e estreita.
  try {
    const { data } = await supabase.rpc('resolve_supplier_custom_domain', {
      p_hostname: hostname,
    });
    const row = (data as Array<{ slug: string; name: string }> | null)?.[0];
    if (row?.slug) {
      slug = row.slug;
      name = row.name ?? null;
    }
  } catch (error) {
    console.error('WhiteLabelBranding: falha ao resolver domínio próprio', error);
  }

  // 2) Subdomínio de fornecedor ou slug presente na URL.
  if (!slug) {
    if (hostname.endsWith('.ameup.com.br')) {
      slug = hostname.replace('.ameup.com.br', '');
    } else {
      slug = slugFromPath();
    }
  }

  if (!slug) return null;

  try {
    const { data } = await (supabase as any)
      .from('suppliers_public')
      .select('slug, name, avatar_url')
      .eq('slug', slug)
      .maybeSingle();

    if (!data) return { slug, name, logoUrl: null };

    return {
      slug: data.slug ?? slug,
      name: data.name ?? name,
      logoUrl: data.avatar_url ?? null,
    };
  } catch (error) {
    console.error('WhiteLabelBranding: falha ao carregar identidade pública', error);
    return { slug, name, logoUrl: null };
  }
};

export const WhiteLabelBrandingProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const whiteLabel = typeof window !== 'undefined' && isWhiteLabelHost();

  const cached = useMemo(() => {
    if (!whiteLabel) return null;
    try {
      return loadSupplierCustomization();
    } catch {
      return null;
    }
  }, [whiteLabel]);

  const [state, setState] = useState<WhiteLabelBranding>(() => ({
    isWhiteLabel: whiteLabel,
    supplierSlug: cached?.supplierSlug ?? null,
    supplierName: cached?.supplierName ?? null,
    logoUrl: cached?.logoUrl ?? null,
    isResolving: whiteLabel,
  }));

  // Limpeza síncrona: garante que a marca AmeUp nunca chegue a ser pintada.
  useLayoutEffect(() => {
    if (!whiteLabel) return;
    stripAmeupHeadAssets();
  }, [whiteLabel]);

  // Resolve a identidade do fornecedor uma única vez por host.
  useEffect(() => {
    if (!whiteLabel) return;
    let cancelled = false;

    (async () => {
      const identity = await resolveSupplierIdentity(window.location.hostname);
      if (cancelled) return;

      if (!identity) {
        setState((prev) => ({ ...prev, isResolving: false }));
        return;
      }

      setState({
        isWhiteLabel: true,
        supplierSlug: identity.slug,
        supplierName: identity.name,
        logoUrl: identity.logoUrl,
        isResolving: false,
      });

      // Alimenta o cache usado pelas telas seguintes da jornada
      // (checkout, contrato, confirmação, parcelas, cobranças adicionais).
      saveSupplierCustomization({
        supplierSlug: identity.slug ?? undefined,
        supplierName: identity.name ?? undefined,
        ...(identity.logoUrl ? { logoUrl: identity.logoUrl } : {}),
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [whiteLabel]);

  // Aplica ícone, título, manifesto e cor de tema do fornecedor.
  useEffect(() => {
    if (!whiteLabel) return;

    stripAmeupHeadAssets();

    const created: HTMLElement[] = [];
    const { logoUrl, supplierName } = state;

    if (logoUrl) {
      const type = guessIconMimeType(logoUrl);
      (['icon', 'shortcut icon', 'apple-touch-icon'] as const).forEach((rel) => {
        const link = document.createElement('link');
        link.setAttribute('rel', rel);
        link.setAttribute('href', logoUrl);
        if (type && rel !== 'apple-touch-icon') link.setAttribute('type', type);
        link.setAttribute(MARKER, 'true');
        document.head.appendChild(link);
        created.push(link);
      });
    }

    if (supplierName && !document.title.includes(supplierName)) {
      document.title = supplierName;
    }

    let manifestUrl: string | null = null;
    if (supplierName) {
      try {
        const colors = cached?.accentColor;
        const manifest = {
          name: supplierName,
          short_name: supplierName.slice(0, 12),
          start_url: '/',
          display: 'standalone',
          background_color: '#ffffff',
          theme_color: colors || '#111111',
          icons: logoUrl
            ? [
                {
                  src: logoUrl,
                  sizes: '192x192',
                  type: guessIconMimeType(logoUrl) || 'image/png',
                },
                {
                  src: logoUrl,
                  sizes: '512x512',
                  type: guessIconMimeType(logoUrl) || 'image/png',
                },
              ]
            : [],
        };
        const blob = new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' });
        manifestUrl = URL.createObjectURL(blob);
        const link = document.createElement('link');
        link.setAttribute('rel', 'manifest');
        link.setAttribute('href', manifestUrl);
        link.setAttribute(MARKER, 'true');
        document.head.appendChild(link);
        created.push(link);

        if (colors) {
          document
            .querySelectorAll<HTMLMetaElement>('meta[name="theme-color"], meta[name="msapplication-TileColor"]')
            .forEach((el) => el.setAttribute('content', colors));
        }
      } catch (error) {
        console.error('WhiteLabelBranding: falha ao gerar manifesto', error);
      }
    }

    return () => {
      created.forEach((el) => el.parentElement?.removeChild(el));
      if (manifestUrl) URL.revokeObjectURL(manifestUrl);
    };
  }, [whiteLabel, state.logoUrl, state.supplierName, cached?.accentColor]);

  return (
    <WhiteLabelBrandingContext.Provider value={state}>
      {children}
    </WhiteLabelBrandingContext.Provider>
  );
};

export default WhiteLabelBrandingProvider;
